import { type ReactNode, type RefObject, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useAnchoredPosition } from '@shared/lib/anchor-position';
import { cn } from '@shared/lib/cn';
import { usePortalContainer } from '@shared/lib/portal';

import { LISTBOX_POPUP_GAP_PX } from './listbox.constant';

export interface ListboxPopupProps {
  /** 목록을 붙일 기준 요소(트리거). 목록은 그 아래에 같은 너비로 뜬다 */
  anchorRef: RefObject<HTMLElement | null>;
  /** portal 대신 제자리(문서 흐름 안, 트리거 아래)에 그린다(fe-rules 3절) */
  inline?: boolean;
  /** 떠 있는 틀의 ref — 밖을 눌렀는지 가릴 때 쓴다 */
  popupRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  children: ReactNode;
}

/**
 * 트리거 아래에 목록을 띄우는 틀. Select · MultiSelect 가 쓴다.
 * 기본은 portal(`usePortalContainer` — 겹쳐 뜨는 창 안이면 그 창의 덮개 틀, 아니면 `document.body`) + fixed 위치
 * (스크롤 · 크기 변경을 따라간다 — `useAnchoredPosition`: 화면 아래가 모자라면 트리거 위로, 그래도 모자라면 줄인다),
 * `inline` 이면 제자리에 그린다.
 */
export function ListboxPopup({
  anchorRef,
  inline = false,
  popupRef,
  className,
  children,
}: ListboxPopupProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const ref = popupRef ?? innerRef;
  const container = usePortalContainer();

  // 아래가 모자라면 위로 뒤집고, 양쪽 다 모자라면 남은 높이로 줄인다(목록이 안에서 스크롤)
  useAnchoredPosition(anchorRef, ref, {
    gap: LISTBOX_POPUP_GAP_PX,
    enabled: !inline,
    matchWidth: true,
    limitHeight: true,
  });

  if (inline) {
    return (
      <div ref={ref} data-slot="listbox-popup" className={cn('w-full', className)}>
        {children}
      </div>
    );
  }
  return createPortal(
    <div
      ref={ref}
      data-slot="listbox-popup"
      className={cn('invisible fixed top-0 left-0 z-(--z-dropdown) flex flex-col', className)}
    >
      {children}
    </div>,
    container,
  );
}
